import * as XLSX from "xlsx";
import { Waypoint } from "../types/types";

export interface WaypointLocalizedExport {
    language: string;
    name?: string | null;
    content?: string | null;
}

export interface WaypointExportRow {
    position: number;
    latitude: number;
    longitude: number;
    localized: WaypointLocalizedExport[];
}

export interface LanguageCodeMapping {
    id: string;
    code: string;
}

const resolveLanguageCode = (language: string, idToCode: Map<string, string>): string => {
    return (idToCode.get(language) || language).toUpperCase();
};

export const buildWaypointExportRows = (
    waypoints: Waypoint[],
    languageMappings: LanguageCodeMapping[],
): WaypointExportRow[] => {
    const idToCode = new Map(languageMappings.map((lang) => [lang.id, lang.code]));

    return waypoints.map((wpt) => ({
        position: wpt.position,
        latitude: wpt.point?.lat ?? 0,
        longitude: wpt.point?.lng ?? 0,
        localized: (wpt.waypointLocalized ?? []).map((loc) => ({
            language: resolveLanguageCode(loc.language, idToCode),
            name: loc.name,
            content: loc.fullText,
        })),
    }));
};

const hasLocalizedContent = (name?: string | null, content?: string | null): boolean => {
    return Boolean((name && name.trim() !== "") || (content && content.trim() !== ""));
};

const collectLanguageCodesWithContent = (waypoints: WaypointExportRow[], idToCode: Map<string, string>): string[] => {
    const codes = new Set<string>();

    for (const wpt of waypoints) {
        for (const loc of wpt.localized) {
            if (!loc.language || !hasLocalizedContent(loc.name, loc.content)) continue;
            codes.add(resolveLanguageCode(loc.language, idToCode));
        }
    }

    return [...codes].sort((a, b) => a.localeCompare(b));
};

const buildLocalizedByCode = (
    localized: WaypointLocalizedExport[],
    idToCode: Map<string, string>,
): Map<string, { name: string; content: string }> => {
    const byCode = new Map<string, { name: string; content: string }>();

    for (const loc of localized) {
        const code = resolveLanguageCode(loc.language, idToCode);
        byCode.set(code, {
            name: loc.name ?? "",
            content: loc.content ?? "",
        });
    }

    return byCode;
};

export const exportWaypointsToExcel = ({
    waypoints,
    languageMappings,
    filename,
}: {
    waypoints: WaypointExportRow[];
    languageMappings: LanguageCodeMapping[];
    filename: string;
}): void => {
    const idToCode = new Map(languageMappings.map((lang) => [lang.id, lang.code]));
    const languageCodes = collectLanguageCodesWithContent(waypoints, idToCode);

    const sortedWaypoints = [...waypoints].sort((a, b) => a.position - b.position);

    const rows = sortedWaypoints.map((wpt) => {
        const localizedByCode = buildLocalizedByCode(wpt.localized, idToCode);
        const row: Record<string, string | number> = {
            position: wpt.position,
            coord: `${wpt.latitude}, ${wpt.longitude}`,
        };

        for (const code of languageCodes) {
            const localized = localizedByCode.get(code);
            row[`name ${code}`] = localized?.name ?? "";
            row[`content ${code}`] = localized?.content ?? "";
        }

        return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Waypoints");
    XLSX.writeFile(workbook, filename);
};
